import React, { useState, useEffect } from "react";
import { useAxios } from "../hooks/axios";
import useSWR from "swr";
import { API } from "../config";
import {
  Table,
  ScrollArea,
  Text,
  Box,
  createStyles,
  Loader,
  Card,
  Group,
  Button,
  Title,
  Modal,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  Card: CardInfo[];
}

interface CardInfo {
  cardAlias: string;
  ActivityLog: ActivityLog[];
}

interface ActivityLog {
  action: string;
  createdAt: string;
  updatedAt: string;
  terminalId: string;
  data: any;
  Terminal?: {
    id: string;
    name: string;
    Location?: {
      id: string;
      name: string;
      description?: string;
    };
  };
}

const useStyles = createStyles((theme) => ({
  container: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    padding: theme.spacing.md,
  },
  tableContainer: {
    overflowX: "auto",
  },
  responsiveTable: {
    minWidth: "800px",
    [theme.fn.smallerThan("sm")]: {
      minWidth: "600px",
      "& th, & td": {
        padding: "8px 4px",
      },
    },
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
}));

const fetcher = async (url: string) => {
  const pin = localStorage.getItem("superPin") || "";
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-pin": pin,
    },
  }).then((res) => res.json());

  console.log(response);

  if (response?.statusCode === 401) {
    localStorage.removeItem("superPin");
    // refresh page
    window.location.reload();
  } else {
    return response;
  }
};

export const UserOverviewPage = () => {
  const { classes } = useStyles();
  const [pinModalOpened, setPinModalOpened] = useState(false);
  const [pin, setPin] = useState<string>(
    localStorage.getItem("superPin") || ""
  );
  const [pinInput, setPinInput] = useState<string>("");
  const [pinError, setPinError] = useState<string>("");

  const { data, error, isLoading, mutate } = useSWR<User[]>(
    pin ? `${API.HOST}/api/overview` : null,
    fetcher
  );

  useEffect(() => {
    if (!pin) {
      setPinModalOpened(true);
    }
  }, [pin]);

  const handlePinSubmit = () => {
    if (pinInput.trim() === "") {
      setPinError("PIN cannot be empty");
      return;
    }
    localStorage.setItem("superPin", pinInput);
    setPin(pinInput);
    setPinModalOpened(false);
    setPinError("");
  };

  const handleLogout = () => {
    localStorage.removeItem("superPin");
    setPin("");
    setPinInput("");
    setPinModalOpened(true);
  };

  if (isLoading) {
    return (
      <Box className={classes.loaderContainer}>
        <Loader />
      </Box>
    );
  }

  if (error) {
    notifications.show({
      title: "Error",
      message: "Failed to fetch data",
      color: "red",
    });
    return null;
  }

  return (
    <Box className={classes.container}>
      <Group position="apart" mb="md">
        <Title order={3}>User Overview</Title>
        <Button variant="outline" size="xs" onClick={handleLogout}>
          Logout
        </Button>
      </Group>

      <Text size="sm" mb="md">
        User Count: {data?.length} <br />
        User Checked in Count:
        {data?.filter((user) => user.Card.length > 0)?.length}
      </Text>

      <ScrollArea className={classes.tableContainer}>
        <Table
          className={classes.responsiveTable}
          highlightOnHover
          striped
          withColumnBorders
        >
          <thead>
            <tr>
              <th>#</th>
              <th>Email</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Linked Card Alias</th>
              <th>Locations Visited</th>
              <th>Check-In At</th>
              <th>Activity</th>
            </tr>
          </thead>
          <tbody>
            {data
              ?.filter((user) => user.Card.length > 0)
              .sort((userA, userB) => {
                // Get all activity logs for both users
                const userAActivity = userA.Card.map(card => card.ActivityLog).flat();
                const userBActivity = userB.Card.map(card => card.ActivityLog).flat();
                
                // Find the latest activity for each user based on updatedAt or createdAt
                const latestActivityA = userAActivity.length > 0 
                  ? Math.max(...userAActivity.map(activity => new Date(activity.updatedAt || activity.createdAt).getTime()))
                  : 0;
                const latestActivityB = userBActivity.length > 0
                  ? Math.max(...userBActivity.map(activity => new Date(activity.updatedAt || activity.createdAt).getTime()))
                  : 0;
                
                // Sort in descending order (most recent first)
                return latestActivityB - latestActivityA;
              })
              .map((user, ix) => {
                const userCards = user.Card.map((card) => card.cardAlias);
                const userCardsActivity = user.Card.map(
                  (card) => card.ActivityLog
                );
                const userAllActivity = userCardsActivity.flat();

                // Also sort each user's activities by most recent first
                const userCheckIn = userAllActivity
                  .filter((activity) => activity.action === "CARD_CHECK_IN")
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  );

                // Get all activities with terminal info, but group by location ID instead of terminal ID
                const userStages = userAllActivity
                  .filter(
                    (activity) =>
                      activity.action === "CARD_CHECKPOINT_ACQUISITION" ||
                      activity.action ===
                        "CARD_CHECKPOINT_PURCHASE_ACQUISITION" ||
                      activity.action === "PAYMENT"
                  )
                  .sort(
                    (a, b) =>
                      new Date(b.createdAt).getTime() -
                      new Date(a.createdAt).getTime()
                  )
                  .reduce((acc: ActivityLog[], current) => {
                    // Get location ID from terminal if available
                    const locationId = current.Terminal?.Location?.id || current.terminalId;
                    
                    // Find existing entry with the same location ID
                    const existing = acc.find(item => {
                      const itemLocationId = item.Terminal?.Location?.id || item.terminalId;
                      return itemLocationId === locationId;
                    });
                    
                    if (existing) {
                      if (!Array.isArray(existing.data)) {
                        existing.data = [];
                      }
                      existing.data = [
                        ...existing.data,
                        ...(Array.isArray(current.data) ? current.data : []),
                      ];
                      existing.updatedAt = current.updatedAt;
                    } else {
                      current.data = Array.isArray(current.data)
                        ? current.data
                        : [];
                      acc.push(current);
                    }
                    return acc;
                  }, []);

                return (
                  <tr key={user.id}>
                    <td>{ix + 1}</td>
                    <td>{user.email}</td>
                    <td>{user.firstName}</td>
                    <td>{user.lastName}</td>
                    <td>
                      {userCards.map((card, index) => (
                        <React.Fragment key={index}>
                          {card}
                          {index < userCards.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </td>
                    <td>
                      <Text weight={600}>
                        {/* Count unique locations by their IDs */}
                        {Array.from(new Set(userStages.map(stage => {
                          // Get location ID from terminal if available
                          return stage.Terminal?.Location?.id || stage.terminalId;
                        }))).length}
                      </Text>
                      {userStages.length > 0 && (
                        <Text size="xs" color="dimmed">
                          {Array.from(new Set(userStages.map(stage => 
                            stage.Terminal?.Location?.name || stage.Terminal?.name || "Unknown"
                          ))).join(", ")}
                        </Text>
                      )}
                    </td>
                    <td>
                      {userCheckIn[0]?.createdAt
                        ? new Date(userCheckIn[0].createdAt).toLocaleTimeString(
                            "nl-NL",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )
                        : "N/A"}
                    </td>
                    <td>
                      <Button
                        component="a"
                        href={`/activity?id=${user.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </ScrollArea>

      <Modal
        opened={pinModalOpened}
        onClose={() => {}}
        title="Enter Admin PIN"
        withCloseButton={false}
        centered
      >
        <TextInput
          label="PIN"
          placeholder="Enter PIN"
          type="password"
          value={pinInput}
          onChange={(e) => setPinInput(e.currentTarget.value)}
          error={pinError}
        />
        <Group position="right" mt="md">
          <Button onClick={handlePinSubmit}>Submit</Button>
        </Group>
      </Modal>
    </Box>
  );
};
